import { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { sendSigningOtp, verifySigningOtp, type SigningPlan } from './signingFlowApi';

const card = 'bg-white rounded-2xl border border-black/[0.08] shadow-sm';
const primary =
  'inline-flex justify-center items-center gap-2 px-4 py-3 text-sm font-semibold text-white rounded-xl bg-[#37352F] disabled:opacity-40';

/**
 * Verifica dell'identità con un codice a 6 cifre prima di firmare.
 * Il codice lo crea e lo controlla il server; qui si chiede e si digita.
 */
export function SigningOtpCard({
  token,
  otp,
  onVerified,
  onSkip,
}: {
  token: string;
  otp: SigningPlan['otp'];
  onVerified: () => void;
  onSkip?: () => void;
}) {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const channelLabel = otp.channel === 'sms' ? 'per SMS' : 'via email';

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await sendSigningOtp(token);
      setSentTo(r.destination);
      setDevCode(r.devCode ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await verifySigningOtp(token, code);
      onVerified();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`${card} px-6 sm:px-8 py-5`}>
      <div className="flex items-center gap-2 text-[#37352F] font-semibold">
        <ShieldCheck className="h-5 w-5" />
        Verifica la tua identità
      </div>
      <p className="mt-1 text-sm text-[#6B6762]">
        Ti mandiamo un codice {channelLabel}
        {otp.destination ? <> a <b>{otp.destination}</b></> : null}. Serve a dimostrare che a firmare sei proprio tu.
      </p>

      {!sentTo ? (
        <button type="button" onClick={send} disabled={busy || !otp.available} className={`${primary} mt-4 w-full`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Mandami il codice
        </button>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-[#37352F]">Codice inviato a <b>{sentTo}</b>. Vale 10 minuti.</p>
          {devCode && (
            <p className="text-xs rounded-lg bg-[#F7F6F3] px-3 py-2 text-[#6B6762]">
              Ambiente di prova, nessun invio: il codice è <b className="tabular-nums">{devCode}</b>
            </p>
          )}
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6 cifre"
            className="block w-full px-4 py-3 text-center text-xl tracking-[0.4em] tabular-nums text-[#37352F] bg-black/[0.03] border border-black/[0.09] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10"
          />
          <button type="button" onClick={verify} disabled={busy || code.length !== 6} className={`${primary} w-full`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Verifica
          </button>
          <button type="button" onClick={send} disabled={busy} className="w-full text-xs text-[#6B6762] underline">
            Non è arrivato? Mandane un altro
          </button>
        </div>
      )}

      {!otp.available && <p className="mt-3 text-xs text-[#9D9892]">L'invio del codice non è ancora attivo per questo documento.</p>}
      {error && <p className="mt-3 text-sm text-[#C4362C]">{error}</p>}
      {onSkip && otp.mode === 'optional' && (
        <button type="button" onClick={onSkip} className="mt-3 w-full text-xs text-[#6B6762] underline">
          Firma senza codice
        </button>
      )}
    </section>
  );
}
